import React from 'react';
import PropTypes from 'prop-types';
import { Tag, Tooltip } from 'antd';
import { captureAnalytics } from '../../actions/preload';
import { formatDateTimeWithoutSeconds } from '../../util/helpers/dateHelpers';
import { receivedDobLastNameSearchPath } from './DedupBadge';
// Deliberately reuses DedupBadge's stylesheet rather than adding a second one:
// the two skip badges are siblings (they can even stack on one inbox row), so
// they must share spacing and tag metrics exactly.
import './DedupBadge.scss';

export const CONTENT_DEDUP_BADGE_LABEL = 'Upload skipped — duplicate fax';

// `sibling_received_at` is an ISO8601 string (unlike task dedup's
// pre-formatted strings), so it is formatted here with the same helper the
// inbox's "Date Received" column uses. A malformed value degrades to no date
// rather than rendering "Invalid Date" at the user.
const formatSiblingReceivedAt = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatDateTimeWithoutSeconds(value);
};

export const buildContentDedupBadgePresentation = (badge, record) => {
  if (!badge || badge.decision !== 'skipped') return null;

  const siblingReceivedAt = formatSiblingReceivedAt(badge.sibling_received_at);
  // Explicit authorization bit from the backend — send_token can be
  // legitimately blank on a visible fax, so it must not be used to infer
  // access. The search link is DOB/name-based and does not need the token.
  const hasSibling = Boolean(badge.sibling_visible);

  const skipSentence = siblingReceivedAt
    ? `This appears to be a copy of a fax received on ${siblingReceivedAt}, so the automatic EHR upload was skipped.`
    : 'This appears to be a copy of a fax received earlier, so the automatic EHR upload was skipped.';
  const closingSentence = hasSibling
    ? 'Click to see the earlier fax for this patient.'
    : 'The earlier fax is outside your access, so it cannot be opened from here.';

  return {
    analyticsEvent: 'content_dedup_matching_records_opened',
    hasSibling,
    // No route opens a received record by send_token alone (the record viewer
    // is keyed on file_token, which this payload does not carry), so we fall
    // back to the same filtered inbox search DedupBadge uses. Content dedup
    // only ever matches inside one patient-identity cohort, so a DOB + last
    // name search always contains the sibling; archived records are included
    // because the earlier fax was usually auto-uploaded and then archived.
    href: hasSibling ? receivedDobLastNameSearchPath(record, undefined, true) : null,
    label: CONTENT_DEDUP_BADGE_LABEL,
    siblingReceivedAt,
    tooltip: [skipSentence, closingSentence].join(' '),
  };
};

const ContentDedupBadge = ({ badge, record }) => {
  const presentation = buildContentDedupBadgePresentation(badge, record);
  if (!presentation) return null;

  const tag = (
    <Tag
      color="volcano"
      style={presentation.href ? { cursor: 'pointer' } : undefined}
    >
      {presentation.label}
    </Tag>
  );

  return (
    <div className="task-dedup-badge content-dedup-badge">
      <Tooltip title={presentation.tooltip}>
        {presentation.href
          ? (
            <a
              className="task-dedup-badge__link"
              href={presentation.href}
              onClick={(event) => {
                // The whole inbox row is clickable; without this the row's own
                // navigation wins over the badge link.
                event.stopPropagation();
                captureAnalytics(presentation.analyticsEvent, {
                  has_sibling_received_at: Boolean(presentation.siblingReceivedAt),
                });
              }}
            >
              {tag}
            </a>
          )
          : <span className="task-dedup-badge__link">{tag}</span>}
      </Tooltip>
    </div>
  );
};

ContentDedupBadge.propTypes = {
  badge: PropTypes.shape({
    decision: PropTypes.string,
    sibling_received_at: PropTypes.string,
    sibling_token: PropTypes.string,
    sibling_visible: PropTypes.bool,
  }),
  record: PropTypes.shape({
    patient_dob: PropTypes.string,
    patient_name: PropTypes.string,
  }).isRequired,
};

ContentDedupBadge.defaultProps = {
  badge: null,
};

export default ContentDedupBadge;
